import PropTypes, { number, string } from 'prop-types';
import React from 'react';
import CheckboxInput from "./CheckboxInput"

/**
 * A controlled group of checkboxes with "Select All" and "Unselect All" buttons.
 * 
 * @param {object} props
 * @param {string} props.id - Checkbox group ID.
 * @param {string} props.name - Checkbox group name.
 * @param {Array} props.options - Array of option objects. Each option should have a "value" and "label" attribute.
 * @param {string} props.label - Checkbox group label.
 * @param {Array} props.selectedValues - Array of selected checkbox values.
 * @param {Function} props.onChange - Function to call on an individual checkbox change (receives the event).
 * @param {Function} props.onSelectAll - Function to call when "Select All" is clicked.
 * @param {Function} props.onUnselectAll - Function to call when "Unselect All" is clicked.
 * @param {boolean} props.groupDisabled - True if checkboxes should be disabled.
 * @param {boolean} props.taller - Optional flag to add extra container height.
 * @param {boolean} props.medium - Optional flag to add extra container height.
 * @param {boolean} props.smallHeader - Optional flag to make header smaller.
 */
const CheckboxGroupScrollable = ({
    id,
    name,
    options,
    label,
    selectedValues,
    onChange,
    onSelectAll,
    onUnselectAll,
    groupDisabled,
    taller = false,
    medium = false,
    smallHeader = false,
}) => {
    return (
        <div className="checkbox-group">
            <div className={`checkbox-group-header ${smallHeader ? "small-header" : ""}`}>
                {label && (
                    <label
                        className="input-label d-inline-block mb-0 me-3"
                        htmlFor={id}
                    >
                        {label}
                    </label>
                )}
                <div>
                    <button
                        type="button"
                        className="btn btn-link text-secondary p-0 me-2"
                        onClick={onSelectAll}
                    >
                        Select All
                    </button>
                    <button
                        type="button"
                        className="btn btn-link text-secondary p-0"
                        onClick={onUnselectAll}
                    >
                        Unselect All
                    </button>
                </div>
            </div>
            <div className={`checkbox-container ${taller ? "taller-container" : ""} ${medium ? "medium-container" : ""}`}>
                {options.map((option, i) => (
                    <CheckboxInput
                        id={`${id}-option[${i}]`}
                        key={i}
                        name={name}
                        value={option.value}
                        label={option.label}
                        checked={selectedValues.includes(option.value)}
                        onChange={onChange}
                        disabled={groupDisabled}
                    />
                ))}
            </div>
        </div>
    );
};

CheckboxGroupScrollable.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([string, number]).isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    label: PropTypes.string,
    selectedValues: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    onSelectAll: PropTypes.func.isRequired,
    onUnselectAll: PropTypes.func.isRequired,
    groupDisabled: PropTypes.bool,
    taller: PropTypes.bool,
    medium: PropTypes.bool,
    smallHeader: PropTypes.bool,
};

export default CheckboxGroupScrollable;
